export const MAX_AVATAR_BYTES = 20 * 1024 * 1024
export const MAX_COMPRESSED_AVATAR_BYTES = 900 * 1024
export const AVATAR_DIMENSION = 512

const ALLOWED_AVATAR_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
])

/**
 * Client-side pre-check before the multipart upload. The backend re-checks the
 * magic bytes and the 20 MB production limit, so this only shortens the
 * feedback loop and never becomes an authorization boundary.
 */
export function validateAvatarFile(file: File): string | null {
  if (file.size === 0) return 'profileSocial.profile.avatarErrors.empty'
  if (file.size > MAX_AVATAR_BYTES) {
    return 'profileSocial.profile.avatarErrors.tooLarge'
  }
  if (file.type && !ALLOWED_AVATAR_TYPES.has(file.type)) {
    return 'profileSocial.profile.avatarErrors.type'
  }
  return null
}

function loadBitmap(file: File): Promise<ImageBitmap> {
  if (typeof createImageBitmap !== 'function') {
    return Promise.reject(new Error('unsupported'))
  }
  return createImageBitmap(file)
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality))
}

/**
 * Web parity with the Flutter client: centre-crop to a square, downscale to
 * 512 px and re-encode as JPEG 0.65. If the browser cannot decode or encode
 * (older engines, jsdom, private-mode restrictions), the original validated
 * file is uploaded and the server's safe re-encode remains authoritative.
 */
export async function prepareAvatarFile(file: File): Promise<File> {
  if (typeof document === 'undefined') return file
  const probe = document.createElement('canvas')
  if (typeof probe.toBlob !== 'function') return file

  try {
    const bitmap = await Promise.race([
      loadBitmap(file),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), 4_000),
      ),
    ])
    try {
      const side = Math.min(bitmap.width, bitmap.height)
      const sourceX = Math.max(0, Math.floor((bitmap.width - side) / 2))
      const sourceY = Math.max(0, Math.floor((bitmap.height - side) / 2))
      const dimension = Math.min(AVATAR_DIMENSION, side)
      const canvas = document.createElement('canvas')
      canvas.width = dimension
      canvas.height = dimension
      const context = canvas.getContext('2d')
      if (!context) return file
      context.drawImage(
        bitmap,
        sourceX,
        sourceY,
        side,
        side,
        0,
        0,
        dimension,
        dimension,
      )
      const blob = await canvasToBlob(canvas, 'image/jpeg', 0.65)
      if (!blob || blob.size === 0) return file
      if (blob.size > MAX_COMPRESSED_AVATAR_BYTES && file.size <= blob.size) {
        return file
      }
      return new File([blob], 'avatar.jpg', {
        type: 'image/jpeg',
        lastModified: Date.now(),
      })
    } finally {
      bitmap.close()
    }
  } catch {
    return file
  }
}
